/**
 * ExchangeContextPanel — surfaces Catchball context during session creation
 * (Canon 23 §9.3, Gap 4).
 *
 * When the selected cycle has an active Catchball loop, this panel shows the
 * Request intent (and revision framing when the proposal was returned) so the
 * person configuring the session understands what the work is responding to.
 * Renders nothing when the exchange state is not_applicable.
 */

import { ArrowDownCircle, RotateCcw, ExternalLink } from 'lucide-react';
import { useExchangeContext } from '../../../hooks/prioritization/useExchangeContext';

export interface ExchangeContextPanelProps {
  cycleId: string | null | undefined;
}

export function ExchangeContextPanel({ cycleId }: ExchangeContextPanelProps) {
  const { exchangeState, activeLoop, intent } = useExchangeContext(cycleId);

  if (exchangeState === 'not_applicable' || !activeLoop) return null;

  const isRevision = exchangeState === 'revision_received';

  return (
    <div
      className={` border p-4 text-sm ${
        isRevision
          ? 'border-[var(--mustard)] bg-[var(--mustard-dim)] dark:border-[var(--mustard)]/50 dark:bg-[var(--mustard)]/30'
          : 'border-[var(--slate)] bg-[var(--slate-dim)] dark:border-[var(--slate)]/50 dark:bg-[var(--slate)]/30'
      }`}
    >
      <div className="flex items-center gap-2 font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
        {isRevision ? (
          <RotateCcw className="h-4 w-4 text-[var(--mustard)] dark:text-[var(--mustard)]" />
        ) : (
          <ArrowDownCircle className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
        )}
        {isRevision
          ? `Revision requested — loop ${activeLoop.loopNumber}`
          : `Responding to a request — loop ${activeLoop.loopNumber}`}
      </div>

      <p className="mt-1 text-[var(--text-sec)] dark:text-[var(--text-sec)]">
        {isRevision
          ? 'The proposal was returned with revision guidance. This session’s work responds to that return.'
          : 'This cycle has an active Catchball request. This session’s work responds to that intent.'}
      </p>

      {intent?.strategicContext && (
        <div className="mt-3">
          <div className="text-xs font-medium uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            Strategic context
          </div>
          <p className="mt-0.5 text-[var(--text-sec)] dark:text-[var(--text-sec)]">{intent.strategicContext}</p>
        </div>
      )}

      {intent && intent.asks.length > 0 && (
        <div className="mt-3">
          <div className="text-xs font-medium uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            Asks
          </div>
          <ul className="mt-0.5 list-disc pl-5 text-[var(--text-sec)] dark:text-[var(--text-sec)]">
            {intent.asks.map((ask, i) => (
              <li key={i}>{ask}</li>
            ))}
          </ul>
        </div>
      )}

      {intent && intent.constraints.length > 0 && (
        <div className="mt-3">
          <div className="text-xs font-medium uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            Constraints
          </div>
          <ul className="mt-0.5 list-disc pl-5 text-[var(--text-sec)] dark:text-[var(--text-sec)]">
            {intent.constraints.map((constraint, i) => (
              <li key={i}>{constraint}</li>
            ))}
          </ul>
        </div>
      )}

      {intent?.guardrails && intent.guardrails.length > 0 && (
        <div className="mt-3">
          <div className="text-xs font-medium uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            Guardrails
          </div>
          <ul className="mt-0.5 list-disc pl-5 text-[var(--text-sec)] dark:text-[var(--text-sec)]">
            {intent.guardrails.map((guardrail, i) => (
              <li key={i}>{guardrail}</li>
            ))}
          </ul>
        </div>
      )}

      {intent?.responseGuidance && (
        <div className="mt-3">
          <div className="text-xs font-medium uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            What a good response looks like
          </div>
          <p className="mt-0.5 text-[var(--text-sec)] dark:text-[var(--text-sec)]">{intent.responseGuidance}</p>
        </div>
      )}

      <a
        href={`/prioritization/cascade/workbench/${activeLoop.cycleId}/${activeLoop.id}`}
        className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)] dark:text-[var(--text-ter)]"
      >
        Open the loop workbench
        <ExternalLink className="h-3 w-3" />
      </a>
    </div>
  );
}
